export interface SchwabLinkProps {
  href: string;
  /** Short visible tooltip (rendered as the `title` attribute) for pointer/hover users. */
  label: string;
  /** Full accessible name, rendered as sr-only text -- this is what a screen reader announces. */
  ariaLabel: string;
}

/**
 * Compact icon-only external link to a Schwab page (research, options
 * ticket). Icon-only by design: every placement is inline beside a symbol or
 * table row and must not widen a column or wrap on mobile (Todd is a mobile
 * user). The ↗ glyph is the only visible content; `ariaLabel` supplies the
 * accessible name via sr-only text rather than a visible label.
 */
export function SchwabLink({ href, label, ariaLabel }: SchwabLinkProps) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      title={label}
      // A deep link must never bubble into a row's own click handler --
      // every placement is inline inside a clickable row/card (SPA-navigate
      // on click, or a toggle-expand), and this is a completely separate
      // action (open Schwab in a new tab). Never preventDefault here: that
      // would cancel the anchor's own native new-tab open. This is the ONE
      // place this guard lives -- hosts must not also stopPropagation on a
      // wrapper around SchwabLink; that would be a second, redundant
      // mechanism doing the same job.
      onClick={(e) => e.stopPropagation()}
      // min-w-8/min-h-8 (32px) clears the WCAG 2.5.8 AA 24px minimum with
      // margin; a matching negative margin keeps the extra hit area from
      // shifting surrounding inline content or widening a table row -- the
      // anchor's own box (and so its clickable area) stays 32x32 regardless
      // of the margin pulling it closer to its neighbors in flow.
      className="inline-flex min-w-8 min-h-8 items-center justify-center p-1 -m-1 rounded text-gray-400 hover:text-brand-600 hover:bg-gray-100 transition-colors"
    >
      <span aria-hidden="true">↗</span>
      <span className="sr-only">{ariaLabel}</span>
    </a>
  );
}
